import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import {
    fetchWaitlistAddons,
    fetchWaitlistClient,
    fetchWaitlistPetServices,
    WaitlistFormAddonOption,
    WaitlistFormClient,
    WaitlistFormServiceOption
} from 'actions/waitlists/WaitlistFormApi';
import Client from '@spike/client-model';
import { fetchPetsThunk } from '@spike/pets-action';
import { MarketplaceSchedule } from '@spike/model';
import Button from 'components/UI/V3/Button/Button';
import WaitlistClientWindow from 'components/Waitlist/WaitlistClientWindow/WaitlistClientWindow';
import { DrawerBody } from 'components/UI/V3/Drawer/Body/DrawerBody';
import { DrawerHeader } from 'components/UI/V3/Drawer/Header/DrawerHeader';
import { Textarea } from 'components/UI/V3/Forms/Textarea/Textarea';
import {
    getFirstWaitlistDate,
    WaitlistFormPet,
    WaitlistFormService,
    WaitlistFormValues
} from 'components/Waitlist/WaitlistForm/waitlistFormUtils';
import { fetchStaffThunk } from 'actions/staff/StaffActions';
import { Staff } from 'model/Staff';
import { useApiClientWrapper } from 'hooks';
import { FunctionComponent, ReactNode, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import useWaitlistClientData from './useWaitlistClientData';
import styles from './WaitlistEditor.module.scss';
import WaitlistPetServicePicker, { WaitlistPetServicePickerResult } from './WaitlistPetServicePicker';
import WaitlistPetServicesCard from './WaitlistPetServicesCard';
import WaitlistPreferencesFields from './WaitlistPreferencesFields';

interface PickerState {
    /** Pet already in the waitlist whose services are added or edited. */
    petId?: number;
    serviceIndex?: number;
    /** Pet preselected (but changeable) when adding a new pet. */
    suggestedPetId?: number;
    /** Only add-ons are added to one of the pet's services. */
    addonsOnly?: boolean;
}

interface WaitlistEditorProps {
    title: string;
    marketplaceId?: number;
    timeZone: string;
    schedule?: MarketplaceSchedule;
    values: WaitlistFormValues;
    /** Pet the client was found by; preselected in the first "Pets & Services" step. */
    suggestedPetId?: number;
    errors: Array<string>;
    /** Content above the pets (location, client); receives the loaded client profile. */
    renderTop: (client?: Client) => ReactNode;
    footer: ReactNode;
    onBack?: () => void;
    onChange: (changes: Partial<WaitlistFormValues>) => void;
}

// Body shared by Add Waitlist and Waitlist Detail: pets and services, preferences and notes,
// plus the "Pets & Services" step that adds or edits a service.
export const WaitlistEditor: FunctionComponent<WaitlistEditorProps> = ({
    title,
    marketplaceId,
    timeZone,
    schedule,
    values,
    suggestedPetId,
    errors,
    renderTop,
    footer,
    onBack,
    onChange
}) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const currentMarketplaceId = useSelector<RootState, number | undefined>(state => state.marketplace.marketplace.id);
    const locationStaff = useSelector<RootState, Array<Staff>>(state => state.staff.staff);

    const { client, pets: profiles } = useWaitlistClientData(values.customerId);

    // undefined while loading.
    const [clientPets, setClientPets] = useState<WaitlistFormClient['pets']>();
    // Bumped to load the client pets again after one is added.
    const [clientPetsVersion, setClientPetsVersion] = useState(0);
    const [addingPet, setAddingPet] = useState(false);
    const [petServices, setPetServices] = useState<Record<number, Array<WaitlistFormServiceOption>>>({});
    const [addonOptions, setAddonOptions] = useState<Array<WaitlistFormAddonOption>>([]);
    const [picker, setPicker] = useState<PickerState>();
    const errorsRef = useRef<HTMLUListElement>(null);

    // The staff store only has the current location: other locations can't pick a staff.
    const staff =
        marketplaceId === currentMarketplaceId ? locationStaff.filter(member => member.active && !member.deleted) : [];

    // Falls back to the name from the API when the staff is not in the location staff list.
    const getStaffName = (service: WaitlistFormService) => {
        const staffMember = staff.find(member => member.id === service.preferredStaffId);

        return staffMember
            ? `${staffMember.person.firstName} ${staffMember.person.lastName}`
            : service.preferredStaffId !== null
            ? service.preferredStaffName
            : null;
    };

    // The errors render below the notes, under the sticky footer: bring them into view after a failed save.
    useEffect(() => {
        errors.length > 0 && errorsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [errors]);

    // Bookings doesn't load the staff list on its own.
    useEffect(() => {
        locationStaff.length === 0 && dispatch(fetchStaffThunk());
    }, []);

    useEffect(() => setClientPets(undefined), [marketplaceId, values.customerId]);

    useEffect(() => {
        if (!marketplaceId || !values.customerId) {
            setClientPets([]);
            return;
        }

        fetchWaitlistClient(marketplaceId, values.customerId)
            .then(loaded => setClientPets(loaded.pets))
            .catch(() => setClientPets([]));
    }, [marketplaceId, values.customerId, clientPetsVersion]);

    // Back from "Add Pet": the new pet is preselected.
    const savedPetHandler = (petId: number) => {
        setAddingPet(false);
        setClientPets(undefined);
        setClientPetsVersion(current => current + 1);
        values.customerId !== undefined && dispatch(fetchPetsThunk(apiClientWrapper, values.customerId));
        setPicker({ suggestedPetId: petId });
    };

    useEffect(() => {
        setPetServices({});

        if (!marketplaceId) {
            return;
        }

        fetchWaitlistAddons(marketplaceId)
            .then(setAddonOptions)
            .catch(() => setAddonOptions([]));
    }, [marketplaceId]);

    // A new client starts on the "Pets & Services" step.
    useEffect(() => {
        if (values.customerId !== undefined && values.pets.length === 0) {
            setPicker({ suggestedPetId });
        }
    }, [values.customerId]);

    const loadPetServices = (petId: number) => {
        if (!marketplaceId) {
            return;
        }

        fetchWaitlistPetServices(marketplaceId, petId)
            .then(services => setPetServices(current => ({ ...current, [petId]: services })))
            .catch(() => setPetServices(current => ({ ...current, [petId]: [] })));
    };

    const removeService = (pet: WaitlistFormPet, index: number) => {
        const services = pet.services.filter((_, current) => current !== index);

        onChange({
            pets:
                services.length === 0
                    ? values.pets.filter(current => current.id !== pet.id)
                    : values.pets.map(current => (current.id === pet.id ? { ...pet, services } : current))
        });
    };

    const savePickerHandler = ({ pet, service, serviceIndex }: WaitlistPetServicePickerResult) => {
        const existing = values.pets.find(current => current.id === pet.id);
        const replacedIndex = serviceIndex ?? picker?.serviceIndex;

        const pets = existing
            ? values.pets.map(current =>
                  current.id === pet.id
                      ? {
                            ...current,
                            services:
                                replacedIndex !== undefined
                                    ? current.services.map((currentService, index) =>
                                          index === replacedIndex ? service : currentService
                                      )
                                    : [...current.services, service]
                        }
                      : current
              )
            : [...values.pets, { ...pet, services: [service] }];

        onChange({ pets });
        setPicker(undefined);
    };

    if (picker) {
        const editingPet = values.pets.find(pet => pet.id === picker.petId);
        const selectablePets = editingPet
            ? [editingPet]
            : clientPets?.filter(pet => !values.pets.some(current => current.id === pet.id));

        return (
            <>
                <WaitlistPetServicePicker
                    // A pet added from here is preselected.
                    key={picker.suggestedPetId}
                    pets={selectablePets ?? []}
                    loadingPets={selectablePets === undefined}
                    profiles={profiles}
                    initialPetId={editingPet?.id ?? picker.suggestedPetId}
                    lockPet={editingPet !== undefined}
                    addonsOnly={picker.addonsOnly}
                    initialService={
                        editingPet && picker.serviceIndex !== undefined
                            ? editingPet.services[picker.serviceIndex]
                            : undefined
                    }
                    currentPets={values.pets}
                    petServices={petServices}
                    addonOptions={addonOptions}
                    staff={staff}
                    timeZone={timeZone}
                    schedule={schedule}
                    onLoadPetServices={loadPetServices}
                    onAddPet={clientPets?.length === 0 ? () => setAddingPet(true) : undefined}
                    onBack={() => setPicker(undefined)}
                    onSave={savePickerHandler}
                />

                {addingPet && (
                    <WaitlistClientWindow
                        clientId={values.customerId}
                        onPetSaved={pet => savedPetHandler(pet.id!)}
                        onClose={() => setAddingPet(false)}
                    />
                )}
            </>
        );
    }

    const canAddPet = (clientPets ?? []).some(pet => !values.pets.some(current => current.id === pet.id));

    return (
        <>
            <DrawerHeader
                title={title}
                onBack={onBack}
            />

            <DrawerBody className={styles.body}>
                {renderTop(client)}

                {values.customerId !== undefined && (
                    <>
                        {values.pets.map(pet => (
                            <WaitlistPetServicesCard
                                key={pet.id}
                                pet={pet}
                                profile={profiles.find(profile => profile.id === pet.id)}
                                getStaffName={getStaffName}
                                onRemove={() =>
                                    onChange({ pets: values.pets.filter(current => current.id !== pet.id) })
                                }
                                onAddService={() => setPicker({ petId: pet.id })}
                                onAddAddon={() => setPicker({ petId: pet.id, serviceIndex: 0, addonsOnly: true })}
                                onEditService={index => setPicker({ petId: pet.id, serviceIndex: index })}
                                onRemoveService={index => removeService(pet, index)}
                            />
                        ))}

                        <div>
                            <Button
                                variant="green"
                                size="small"
                                leftIcon={faPlus}
                                label="Add Another Pet"
                                disabled={!canAddPet}
                                onClick={() => setPicker({})}
                            />
                        </div>

                        <WaitlistPreferencesFields
                            firstDate={getFirstWaitlistDate(values.pets, timeZone)}
                            preferredTime={values.preferredTime}
                            validUntil={values.validUntil}
                            timeZone={timeZone}
                            schedule={schedule}
                            onChange={onChange}
                        />

                        <Textarea
                            label="Notes"
                            placeholder="Notes here..."
                            value={values.notes}
                            onChange={event => onChange({ notes: event.target.value })}
                        />
                    </>
                )}

                {errors.length > 0 && (
                    <ul
                        ref={errorsRef}
                        className={styles.errors}
                        role="alert"
                    >
                        {errors.map(error => (
                            <li key={error}>{error}</li>
                        ))}
                    </ul>
                )}
            </DrawerBody>

            {footer}
        </>
    );
};

export default WaitlistEditor;
